import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ONBOARDING_SCREENS, type OnboardingScreen, type OnboardingStateDTO } from '@libs/dtos/merit/onboarding.dto';
import { MeritI18nService } from '@services/merit/merit-i18n.service';
import { LucideModule } from '@shared/modules/lucide.module';
import type { OnboardingFile } from '../onboarding-dropzone/onboarding-dropzone.component';

/** One of the wizard's screens as the review screen renders it (23/09: eight, Documents first). */
export interface ReviewSection {
	step: OnboardingScreen;
	/** 1-based, matching the wizard's numbering. */
	number: number;
	done: boolean;
	/** Flat label/value pairs (label is an i18n key). */
	rows: { label: string; value: string }[];
	/** Repeatable entries (experience, education, languages). */
	items: { title: string; detail: string }[];
	/** Documents holds three: CV, photo and the optional video. */
	files: OnboardingFile[];
	empty: boolean;
}

/**
 * M02 · "Revisar y enviar" (`c-review`).
 *
 * Summary of the ten sections with a jump back into any of them. Submit is
 * enabled only when the server says `canSubmit`; otherwise the `blockingReasons`
 * it returned are listed, so the candidate knows exactly what is missing.
 */
@Component({
	selector: 'app-onboarding-review',
	imports: [TranslocoModule, LucideModule],
	templateUrl: './onboarding-review.component.html',
	styleUrl: './onboarding-review.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingReviewComponent {
	private readonly transloco = inject(TranslocoService);
	private readonly meritI18n = inject(MeritI18nService);

	readonly state = input.required<OnboardingStateDTO>();
	readonly submitting = input(false);
	readonly error = input<string | null>(null);

	/** 0-based screen index (ONBOARDING_SCREENS) to reopen in the wizard. */
	readonly edit = output<number>();
	readonly send = output<void>();

	/** Re-render the imperatively built labels when the locale changes. */
	private readonly lang = toSignal(this.transloco.langChanges$, { initialValue: '' });

	private readonly dash = '—';

	/** Two-digit step number ("01"… "10"). */
	pad(n: number): string {
		return n < 10 ? `0${n}` : String(n);
	}

	/** The last screen, where «Back» returns to. */
	readonly lastScreen = ONBOARDING_SCREENS.length - 1;

	readonly sections = computed<ReviewSection[]>(() => {
		this.lang();
		const state = this.state();
		const done = new Set<string>(state.completedSteps);
		return ONBOARDING_SCREENS.map((step, i) => {
			const section = this.build(step, state);
			return {
				step,
				number: i + 1,
				// Documents is done with the CV and the photo: the video is optional.
				done: step === 'documents' ? done.has('cv') && done.has('photo') : done.has(step),
				rows: section.rows,
				items: section.items,
				files: section.files,
				empty: !section.rows.length && !section.items.length && !section.files.length,
			};
		});
	});

	readonly blockingReasons = computed(() => {
		this.lang();
		return this.state().blockingReasons.map((reason) => this.reasonLabel(reason));
	});

	/**
	 * The API returns i18n keys. They may be fully qualified or bare (e.g.
	 * `videoMissing`), and an unknown one must still read as something — so a
	 * missing translation falls back to the raw value instead of a key on screen.
	 */
	private reasonLabel(reason: string): string {
		for (const key of [reason, `merit.onboarding.blocking.${reason}`]) {
			const value = this.transloco.translate(key);
			if (value && value !== key) return value;
		}
		return reason;
	}

	private build(
		step: OnboardingScreen,
		state: OnboardingStateDTO,
	): {
		rows: { label: string; value: string }[];
		items: { title: string; detail: string }[];
		files: OnboardingFile[];
	} {
		const f = 'merit.onboarding.fields.';
		const opt = (value: string | null | undefined) => (value ? this.meritI18n.option(value) : this.dash);
		const text = (value: string | null | undefined) => (value?.trim() ? value : this.dash);
		const none = { rows: [], items: [], files: [] };

		switch (step) {
			case 'personal': {
				const p = state.personal;
				if (!p) return none;
				return {
					rows: [
						{ label: `${f}fullName`, value: text(p.fullName) },
						{ label: `${f}email`, value: text(p.email) },
						{ label: `${f}phone`, value: text(p.phone) },
						{ label: `${f}birthDate`, value: text(p.birthDate) },
						// El código manda sobre el texto libre: las fichas anteriores a la
						// cascada solo tienen el segundo, y viene en tres formatos.
						{
							label: `${f}nationality`,
							value: this.meritI18n.nationality(p.countryCode, p.nationality) || this.dash,
						},
						{ label: `${f}city`, value: opt(p.city) },
						{ label: `${f}linkedinUrl`, value: text(p.linkedinUrl) },
						{ label: `${f}heightCm`, value: p.heightCm != null ? `${p.heightCm} cm` : this.dash },
					],
					items: [],
					files: [],
				};
			}
			case 'documents':
				return {
					rows: [],
					items: [],
					files: [state.cv, state.photo, state.video].filter((file): file is OnboardingFile => !!file),
				};
			case 'experience':
				return {
					rows: [{ label: `${f}managementExperience`, value: opt(state.managementExperience) }],
					items: state.experience.map((e) => ({
						title: `${this.meritI18n.option(e.position)} · ${e.employerProperty}`,
						detail: [
							this.meritI18n.option(e.department),
							this.meritI18n.option(e.luxuryLevel),
							`${e.fromDate} → ${e.toDate ?? this.transloco.translate('merit.onboarding.fields.currentJob')}`,
						]
							.filter(Boolean)
							.join(' · '),
					})),
					files: [],
				};
			case 'education':
				return {
					rows: [],
					items: state.education.map((e) => ({
						title: e.title,
						detail: [
							this.transloco.translate(`merit.onboarding.educationLevels.${e.level}`),
							e.institution,
							String(e.year),
							e.isCertification ? this.transloco.translate(`${f}isCertification`) : '',
						]
							.filter(Boolean)
							.join(' · '),
					})),
					files: [],
				};
			case 'languages':
				return {
					rows: [],
					items: state.languages.map((l) => ({
						title: this.meritI18n.language(l.language),
						detail: [
							// The level as it was picked (23/09): the CEFR label the platform uses.
							l.cefr
								? this.meritI18n.level(l.cefr)
								: this.transloco.translate(`merit.onboarding.proficiency.${l.level}`),
							l.percent != null ? `${l.percent}%` : '',
						]
							.filter(Boolean)
							.join(' · '),
					})),
					files: [],
				};
			case 'legal': {
				const l = state.legal;
				if (!l) return none;
				const yesNo = l.needsSponsor ? 'merit.onboarding.fields.yes' : 'merit.onboarding.fields.no';
				return {
					rows: [
						{ label: `${f}visaStatus`, value: opt(l.visaStatus) },
						{ label: `${f}emiratesId`, value: text(l.emiratesId) },
						{ label: `${f}needsSponsor`, value: this.transloco.translate(yesNo) },
						{ label: `${f}currentEmployment`, value: text(l.currentEmployment) },
						{ label: `${f}drivingLicence`, value: opt(l.drivingLicence) },
					],
					items: [],
					files: [],
				};
			}
			case 'availability': {
				const a = state.availability;
				if (!a) return none;
				return {
					rows: [
						{ label: `${f}availability`, value: opt(a.availability) },
						{ label: `${f}relocation`, value: opt(a.relocation) },
						{ label: `${f}gccExperience`, value: opt(a.gccExperience) },
						{ label: `${f}salaryExpectation`, value: opt(a.salaryExpectation) },
					],
					items: [],
					files: [],
				};
			}
			case 'preferences': {
				const p = state.preferences;
				if (!p) return none;
				const list = (values: string[] | undefined) =>
					values?.length ? values.map((v) => this.meritI18n.option(v)).join(' · ') : this.dash;
				return {
					rows: [
						{ label: `${f}targetPositions`, value: list(p.targetPositions) },
						{ label: `${f}propertyTypes`, value: list(p.propertyTypes) },
					],
					items: [],
					files: [],
				};
			}
			default:
				return none;
		}
	}
}
